import { useState, useEffect, useCallback } from 'react';
import { Truck, Container, Edit3, Plus, AlertTriangle, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminApi } from '../hooks/useAdminApi';
import { CurrentAssignmentModal } from '../../components/CurrentAssignmentModal';

const tag = (v) => (v.unit_number ? `#${v.unit_number}` : v.plate_number || 'Véhicule');

const MissingNotice = ({ onAccept }) => (
  <div className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-500/10 border-b border-amber-500/30" data-testid="admin-missing-truck-notice">
    <p className="flex items-center gap-2 text-sm text-amber-300">
      <AlertTriangle size={16} /> Aucun véhicule choisi aujourd'hui pour ce chauffeur. Choisissez son camion ou acceptez la situation.
    </p>
    <button onClick={onAccept} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800 text-slate-200 hover:bg-slate-700 transition-colors shrink-0" data-testid="admin-accept-missing-truck">
      <Check size={13} /> Accepter
    </button>
  </div>
);

export const DriverAssignmentChip = ({ driverId }) => {
  const api = useAdminApi();
  const { get, post } = api;
  const [assignment, setAssignment] = useState(null);
  const [open, setOpen] = useState(false);
  const base = `/saas/driver/${driverId}/assignment`;

  const load = useCallback(async () => {
    const r = await get(base);
    setAssignment(r.success ? { truck: r.data.truck, trailers: r.data.trailers || [], missing: r.data.missing_today } : { error: true });
  }, [get, base]);

  useEffect(() => { load(); }, [load]);
  if (!assignment || assignment.error) return null;
  const { truck, trailers, missing } = assignment;

  const accept = async () => {
    const r = await post(`${base}/accept-missing`, {});
    if (r.success) { toast.success('Situation acceptée pour aujourd\'hui'); setOpen(false); load(); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${missing ? 'border-amber-500/70 bg-amber-500/10' : 'border-slate-700 bg-slate-800/60 hover:border-amber-500/60'}`}
        data-testid="admin-driver-assignment-chip"
        title="Modifier le camion et les remorques de ce chauffeur"
      >
        {missing ? <AlertTriangle size={16} className="text-amber-400" /> : <Truck size={16} className="text-amber-500" />}
        {truck ? (
          <span className="font-semibold text-white" data-testid="admin-assignment-truck">{tag(truck)}{truck.is_manual && <span className="ml-1 text-xs text-blue-300">(loué)</span>}</span>
        ) : (
          <span className="text-amber-400" data-testid="admin-assignment-truck">{missing ? "Aucun véhicule choisi aujourd'hui" : 'Aucun camion'}</span>
        )}
        {trailers.map((t) => (
          <span key={t.id} className="flex items-center gap-1 text-slate-300" data-testid={`admin-assignment-trailer-${t.id}`}>
            <Container size={14} className="text-slate-400" />{tag(t)}
          </span>
        ))}
        <span className="flex items-center gap-1 text-xs text-slate-400 ml-1">
          {truck || trailers.length ? <><Edit3 size={13} /> Modifier</> : <><Plus size={13} /> Assigner</>}
        </span>
      </button>
      {open && (
        <CurrentAssignmentModal
          assignment={assignment}
          api={api}
          apiBase={base}
          askKm={false}
          title="Véhicules conduits par ce chauffeur"
          notice={missing ? <MissingNotice onAccept={accept} /> : null}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); load(); }}
        />
      )}
    </>
  );
};
